/**
 * SecurityMonitor Component - ADVANCED SECURITY v2.0
 * Features: Security logs, threat monitoring, IP blocking, auto-block settings,
 * PERMANENT BANS, COUNTRY BLOCKING, HONEYPOT TRAPS, TARPIT, ALERTS
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Lock, Check, X, AlertTriangle, Activity, RefreshCw, Shield, Eye,
  Globe, Skull, Clock, Bell, Ban, Search, MapPin, Zap
} from 'lucide-react';
import OverviewTab from './security/OverviewTab';
import LiveAttacksTab from './security/LiveAttacksTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SecurityMonitor() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  
  // Overview stats
  const [securityLogs, setSecurityLogs] = useState([]);
  const [securityStats, setSecurityStats] = useState({ success: 0, failed: 0, blocked: 0, critical: 0 });
  const [activeSessions, setActiveSessions] = useState([]);
  
  // Threat monitoring
  const [threats, setThreats] = useState([]);
  const [blockedIps, setBlockedIps] = useState([]);
  const [blockIpInput, setBlockIpInput] = useState('');
  
  // ADVANCED FEATURES
  const [permanentBans, setPermanentBans] = useState([]);
  const [blockedCountries, setBlockedCountries] = useState([]);
  const [honeypotVictims, setHoneypotVictims] = useState([]);
  const [honeypotPaths, setHoneypotPaths] = useState([]);
  const [tarpitList, setTarpitList] = useState([]);
  const [alerts, setAlerts] = useState([]);
  
  // Input states
  const [permanentBanIp, setPermanentBanIp] = useState('');
  const [permanentBanReason, setPermanentBanReason] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [tarpitIp, setTarpitIp] = useState('');
  const [tarpitDelay, setTarpitDelay] = useState(5);
  const [ipLookup, setIpLookup] = useState('');
  const [ipLookupResult, setIpLookupResult] = useState(null);
  
  // Lockout
  const [lockoutEmail, setLockoutEmail] = useState('');
  const [lockoutStatus, setLockoutStatus] = useState(null);

  // Auto-block settings
  const [autoBlockSettings, setAutoBlockSettings] = useState({
    enabled: true,
    block_on_critical_attack: true,
    block_on_failed_logins: true,
    failed_login_threshold: 10,
    suspicious_score_threshold: 50,
    block_duration_hours: 24,
    block_on_rate_limit_abuse: true,
    rate_limit_abuse_count: 3
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [logsRes, sessionsRes, threatsRes, bansRes, countriesRes, honeypotRes, tarpitRes, alertsRes] = await Promise.all([
        axios.get(`${API}/auth/security/logs?limit=100`, { withCredentials: true }).catch(() => ({ data: { logs: [] } })),
        axios.get(`${API}/auth/security/active-sessions`, { withCredentials: true }).catch(() => ({ data: { sessions: [] } })),
        axios.get(`${API}/auth/security/threats`, { withCredentials: true }).catch(() => ({ data: { threats: [], blocked_ips: [] } })),
        axios.get(`${API}/auth/security/permanent-bans`, { withCredentials: true }).catch(() => ({ data: { permanent_bans: [] } })),
        axios.get(`${API}/auth/security/blocked-countries`, { withCredentials: true }).catch(() => ({ data: { blocked_countries: [] } })),
        axios.get(`${API}/auth/security/honeypot-victims`, { withCredentials: true }).catch(() => ({ data: { victims: [], honeypot_paths: [] } })),
        axios.get(`${API}/auth/security/tarpit-list`, { withCredentials: true }).catch(() => ({ data: { tarpitted_ips: [] } })),
        axios.get(`${API}/auth/security/alerts`, { withCredentials: true }).catch(() => ({ data: { alerts: [] } }))
      ]);
      
      const logs = logsRes.data?.logs || [];
      setSecurityLogs(logs);
      setActiveSessions(sessionsRes.data?.sessions || []);
      setThreats(threatsRes.data?.threats || []);
      setBlockedIps(threatsRes.data?.blocked_ips || []);
      setPermanentBans(bansRes.data?.permanent_bans || []);
      setBlockedCountries(countriesRes.data?.blocked_countries || []);
      setHoneypotVictims(honeypotRes.data?.victims || []);
      setHoneypotPaths(honeypotRes.data?.honeypot_paths || []);
      setTarpitList(tarpitRes.data?.tarpitted_ips || []);
      setAlerts(alertsRes.data?.alerts || []);
      
      setSecurityStats({
        success: logs.filter(l => l.event_type === 'login_success').length,
        failed: logs.filter(l => l.event_type?.includes('failed')).length,
        blocked: logs.filter(l => l.event_type?.includes('locked') || l.event_type?.includes('blocked')).length,
        critical: logs.filter(l => l.threat_level === 'critical' || l.threat_level === 'high').length
      });
    } catch (e) {
      console.error('Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  // PERMANENT BAN
  const addPermanentBan = async () => {
    if (!permanentBanIp) return toast.error('Enter an IP address');
    try {
      await axios.post(`${API}/auth/security/permanent-ban?ip=${permanentBanIp}&reason=${encodeURIComponent(permanentBanReason || 'Manual ban')}`, {}, { withCredentials: true });
      toast.success(`🚫 IP ${permanentBanIp} PERMANENTLY BANNED!`);
      setPermanentBanIp('');
      setPermanentBanReason('');
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to ban IP');
    }
  };

  const removePermanentBan = async (ip) => {
    try {
      await axios.delete(`${API}/auth/security/permanent-ban?ip=${ip}`, { withCredentials: true });
      toast.success(`Ban removed for ${ip}`);
      fetchAllData();
    } catch (e) {
      toast.error('Failed to remove ban');
    }
  };

  // COUNTRY BLOCKING
  const blockCountry = async () => {
    if (!countryCode || countryCode.length !== 2) return toast.error('Enter 2-letter country code (e.g., CN, RU)');
    try {
      await axios.post(`${API}/auth/security/block-country?country_code=${countryCode.toUpperCase()}`, {}, { withCredentials: true });
      toast.success(`🌍 Country ${countryCode.toUpperCase()} BLOCKED!`);
      setCountryCode('');
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to block country');
    }
  };

  const unblockCountry = async (code) => {
    try {
      await axios.delete(`${API}/auth/security/block-country?country_code=${code}`, { withCredentials: true });
      toast.success(`Country ${code} unblocked`);
      fetchAllData();
    } catch (e) {
      toast.error('Failed to unblock country');
    }
  };

  // TARPIT
  const addToTarpit = async () => {
    if (!tarpitIp) return toast.error('Enter an IP address');
    try {
      await axios.post(`${API}/auth/security/tarpit?ip=${tarpitIp}&delay_seconds=${tarpitDelay}`, {}, { withCredentials: true });
      toast.success(`🐌 IP ${tarpitIp} will be slowed by ${tarpitDelay}s per request!`);
      setTarpitIp('');
      fetchAllData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to tarpit IP');
    }
  };

  const removeFromTarpit = async (ip) => {
    try {
      await axios.delete(`${API}/auth/security/tarpit?ip=${ip}`, { withCredentials: true });
      toast.success(`Tarpit removed for ${ip}`);
      fetchAllData();
    } catch (e) {
      toast.error('Failed to remove tarpit');
    }
  };

  // IP LOOKUP
  const lookupIp = async () => {
    if (!ipLookup) return toast.error('Enter an IP address');
    try {
      const res = await axios.get(`${API}/auth/security/ip-lookup/${ipLookup}`, { withCredentials: true });
      setIpLookupResult(res.data);
    } catch (e) {
      toast.error('Failed to lookup IP');
    }
  };

  // TEMP BLOCK
  const blockIp = async (ip, hours = 24) => {
    try {
      await axios.post(`${API}/auth/security/block-ip?ip=${ip}&duration_hours=${hours}`, {}, { withCredentials: true });
      toast.success(`IP ${ip} blocked for ${hours} hours`);
      fetchAllData();
    } catch (e) {
      toast.error('Failed to block IP');
    }
  };

  const unblockIp = async (ip) => {
    try {
      await axios.post(`${API}/auth/security/unblock-ip?ip=${ip}`, {}, { withCredentials: true });
      toast.success(`IP ${ip} unblocked`);
      fetchAllData();
    } catch (e) {
      toast.error('Failed to unblock IP');
    }
  };

  // LOCKOUT
  const checkLockout = async () => {
    if (!lockoutEmail) return;
    try {
      const res = await axios.get(`${API}/auth/security/lockout-status?email=${lockoutEmail}`, { withCredentials: true });
      setLockoutStatus(res.data);
    } catch (e) {
      toast.error('Failed to check lockout');
    }
  };

  const unlockAccount = async () => {
    try {
      await axios.post(`${API}/auth/security/unlock-account?email=${lockoutEmail}`, {}, { withCredentials: true });
      toast.success('Account unlocked!');
      setLockoutStatus({ ...lockoutStatus, is_locked: false });
    } catch (e) {
      toast.error('Failed to unlock');
    }
  };

  const emergencyReset = async () => {
    if (!window.confirm('⚠️ This will clear ALL security locks and counters. Users who were locked out will be able to login again. Continue?')) {
      return;
    }
    try {
      const res = await axios.post(`${API}/auth/security/emergency-reset`, {}, { withCredentials: true });
      toast.success('All security locks cleared!');
      console.log('Emergency reset result:', res.data);
    } catch (e) {
      toast.error('Failed to reset: ' + (e.response?.data?.detail || e.message));
    }
  };

  // NEW: Fetch live dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [attackerLocations, setAttackerLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const fetchDashboardData = async () => {
    try {
      const res = await axios.get(`${API}/security/dashboard`, { withCredentials: true });
      setDashboardData(res.data);
      
      // Also update honeypot victims from dashboard
      if (res.data.honeypot_victims) {
        setHoneypotVictims(res.data.honeypot_victims);
      }
      if (res.data.permanent_bans) {
        setPermanentBans(res.data.permanent_bans);
      }
    } catch (e) {
      console.error('Failed to load dashboard data');
    }
  };

  const fetchAttackerLocations = async () => {
    setLoadingLocations(true);
    const locations = [];
    
    // Get location for each honeypot victim
    for (const victim of honeypotVictims.slice(0, 20)) { // Limit to 20
      try {
        const res = await axios.get(`${API}/security/ip-info/${victim.ip}`, { withCredentials: true });
        if (res.data.status === 'success') {
          locations.push({
            ...res.data,
            path: victim.path,
            caught_at: victim.caught_at
          });
        }
      } catch (e) {
        console.error('Failed to get location for', victim.ip);
      }
    }
    
    setAttackerLocations(locations);
    setLoadingLocations(false);
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'live', label: 'Live Attacks', icon: Zap },
    { id: 'map', label: 'Attacker Map', icon: MapPin },
    { id: 'permanent', label: 'Permanent Bans', icon: Ban },
    { id: 'countries', label: 'Country Block', icon: Globe },
    { id: 'honeypot', label: 'Honeypot Traps', icon: Skull },
    { id: 'tarpit', label: 'Tarpit', icon: Clock },
    { id: 'lookup', label: 'IP Lookup', icon: Search },
    { id: 'logs', label: 'Audit Log', icon: Activity },
  ];

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap flex items-center gap-2 transition-colors ${
              activeTab === tab.id ? 'bg-accent text-black' : 'bg-secondary hover:bg-accent/20'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.id === 'permanent' && permanentBans.length > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">{permanentBans.length}</span>
            )}
            {tab.id === 'honeypot' && honeypotVictims.length > 0 && (
              <span className="px-1.5 py-0.5 bg-yellow-500 text-black text-xs rounded-full">{honeypotVictims.length}</span>
            )}
          </button>
        ))}
        <button onClick={fetchAllData} disabled={loading} className="px-3 py-2 rounded-xl bg-secondary hover:bg-accent/20">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <OverviewTab
          securityStats={securityStats} permanentBans={permanentBans}
          honeypotVictims={honeypotVictims} blockedCountries={blockedCountries}
          dashboardData={dashboardData} threats={threats}
          blockIpInput={blockIpInput} setBlockIpInput={setBlockIpInput} blockIp={blockIp}
          lockoutEmail={lockoutEmail} setLockoutEmail={setLockoutEmail}
          checkLockout={checkLockout} lockoutStatus={lockoutStatus} unlockAccount={unlockAccount}
          emergencyReset={emergencyReset} setPermanentBanIp={setPermanentBanIp}
          setActiveTab={setActiveTab} setIpLookup={setIpLookup} lookupIp={lookupIp}
        />
      )}

      {/* LIVE ATTACKS TAB */}
      {activeTab === 'live' && (
        <LiveAttacksTab honeypotVictims={honeypotVictims} permanentBans={permanentBans} dashboardData={dashboardData} />
      )}

      {/* ATTACKER MAP TAB - NEW! */}
      {activeTab === 'map' && (
        <div className="space-y-6">
          <div className="bg-card/60 rounded-2xl p-6 border border-blue-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-400" /> 🌍 Attacker Geographic Map
              </h3>
              <Button onClick={fetchAttackerLocations} disabled={loadingLocations} className="bg-blue-500 hover:bg-blue-600">
                {loadingLocations ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
                ) : (
                  <><MapPin className="w-4 h-4 mr-2" /> Load Attacker Locations</>
                )}
              </Button>
            </div>

            {/* World Map Visualization (ASCII style for now) */}
            <div className="bg-secondary/30 rounded-xl p-6 mb-4">
              <div className="text-center mb-4">
                <p className="text-sm text-muted-foreground">
                  {attackerLocations.length > 0 
                    ? `📍 ${attackerLocations.length} attacker locations identified`
                    : 'Click "Load Attacker Locations" to see where attacks are coming from'}
                </p>
              </div>
              
              {/* Countries Summary */}
              {attackerLocations.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  {Object.entries(
                    attackerLocations.reduce((acc, loc) => {
                      acc[loc.country] = (acc[loc.country] || 0) + 1;
                      return acc;
                    }, {})
                  ).sort((a, b) => b[1] - a[1]).map(([country, count], i) => (
                    <div key={i} className="bg-red-500/10 rounded-xl p-4 text-center border border-red-500/20">
                      <p className="text-2xl mb-1">
                        {country === 'United States' ? '🇺🇸' : 
                         country === 'China' ? '🇨🇳' :
                         country === 'Russia' ? '🇷🇺' :
                         country === 'The Netherlands' ? '🇳🇱' :
                         country === 'Germany' ? '🇩🇪' :
                         country === 'France' ? '🇫🇷' :
                         country === 'United Kingdom' ? '🇬🇧' :
                         country === 'India' ? '🇮🇳' :
                         country === 'Brazil' ? '🇧🇷' : '🌍'}
                      </p>
                      <p className="font-bold text-red-400">{count}</p>
                      <p className="text-xs text-muted-foreground truncate">{country}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Detailed Attacker List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              <h4 className="font-medium mb-2">Attacker Details</h4>
              {attackerLocations.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  No location data loaded yet. Click the button above to fetch attacker locations.
                </p>
              ) : (
                attackerLocations.map((loc, i) => (
                  <div key={i} className="p-4 bg-secondary/50 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-xl">
                          {loc.countryCode === 'US' ? '🇺🇸' : 
                           loc.countryCode === 'CN' ? '🇨🇳' :
                           loc.countryCode === 'RU' ? '🇷🇺' :
                           loc.countryCode === 'NL' ? '🇳🇱' :
                           loc.countryCode === 'DE' ? '🇩🇪' : '🌍'}
                        </div>
                        <div>
                          <span className="font-mono font-bold text-red-400">{loc.query}</span>
                          <p className="text-sm">
                            <span className="text-blue-400">{loc.city}, {loc.regionName}</span>
                            <span className="text-muted-foreground"> • {loc.country}</span>
                          </p>
                          <p className="text-xs text-muted-foreground">{loc.isp} ({loc.org})</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-yellow-400 font-mono">{loc.path}</p>
                        <p className="text-xs text-muted-foreground">{new Date(loc.caught_at).toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">Lat: {loc.lat}, Lon: {loc.lon}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* PERMANENT BANS TAB */}
      {activeTab === 'permanent' && (
        <div className="space-y-6">
          <div className="bg-card/60 rounded-2xl p-6 border border-red-500/30">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Ban className="w-6 h-6 text-red-400" /> 🚫 Permanent IP Ban
              <span className="text-sm text-muted-foreground ml-2">They can NEVER access your site again</span>
            </h3>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                placeholder="IP Address (e.g., 123.45.67.89)"
                value={permanentBanIp}
                onChange={(e) => setPermanentBanIp(e.target.value)}
                className="flex-1 px-4 py-3 rounded-xl bg-secondary border border-border/30"
              />
              <input
                type="text"
                placeholder="Reason (optional)"
                value={permanentBanReason}
                onChange={(e) => setPermanentBanReason(e.target.value)}
                className="flex-1 px-4 py-3 rounded-xl bg-secondary border border-border/30"
              />
              <Button onClick={addPermanentBan} className="bg-red-500 hover:bg-red-600 px-6">
                <Ban className="w-4 h-4 mr-2" /> PERMANENT BAN
              </Button>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {permanentBans.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No permanent bans yet</p>
              ) : permanentBans.map((ban, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-red-500/10 rounded-xl border border-red-500/20">
                  <div>
                    <span className="font-mono font-bold text-red-400">{ban.ip}</span>
                    <p className="text-sm text-muted-foreground">{ban.reason || 'No reason'}</p>
                    <p className="text-xs text-muted-foreground">Banned: {new Date(ban.banned_at).toLocaleString()}</p>
                  </div>
                  <Button onClick={() => removePermanentBan(ban.ip)} variant="ghost" className="text-green-400">
                    Unban
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* COUNTRY BLOCKING TAB */}
      {activeTab === 'countries' && (
        <div className="space-y-6">
          <div className="bg-card/60 rounded-2xl p-6 border border-blue-500/30">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Globe className="w-6 h-6 text-blue-400" /> 🌍 Country Blocking
              <span className="text-sm text-muted-foreground ml-2">Block ALL traffic from a country</span>
            </h3>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                placeholder="Country Code (e.g., CN, RU, KP)"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value.toUpperCase())}
                maxLength={2}
                className="w-32 px-4 py-3 rounded-xl bg-secondary border border-border/30 uppercase text-center font-mono text-xl"
              />
              <Button onClick={blockCountry} className="bg-blue-500 hover:bg-blue-600 px-6">
                <Globe className="w-4 h-4 mr-2" /> Block Country
              </Button>
            </div>
            
            <div className="p-4 bg-secondary/30 rounded-xl mb-4">
              <p className="text-sm text-muted-foreground mb-2">Common country codes:</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { code: 'CN', name: 'China' },
                  { code: 'RU', name: 'Russia' },
                  { code: 'KP', name: 'N. Korea' },
                  { code: 'IR', name: 'Iran' },
                  { code: 'VN', name: 'Vietnam' },
                  { code: 'IN', name: 'India' },
                  { code: 'BR', name: 'Brazil' },
                ].map(c => (
                  <button 
                    key={c.code}
                    onClick={() => setCountryCode(c.code)}
                    className="px-3 py-1 bg-secondary rounded-lg text-sm hover:bg-accent/20"
                  >
                    {c.code} - {c.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Blocked Countries ({blockedCountries.length})</h4>
              {blockedCountries.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground">No countries blocked</p>
              ) : blockedCountries.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-blue-500/10 rounded-xl border border-blue-500/20">
                  <div>
                    <span className="font-mono font-bold text-xl">{c.code}</span>
                    <span className="text-sm text-muted-foreground ml-3">{c.reason || 'Blocked'}</span>
                  </div>
                  <Button onClick={() => unblockCountry(c.code)} variant="ghost" className="text-green-400">
                    Unblock
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* HONEYPOT TAB */}
      {activeTab === 'honeypot' && (
        <div className="space-y-6">
          <div className="bg-card/60 rounded-2xl p-6 border border-purple-500/30">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Skull className="w-6 h-6 text-purple-400" /> 🍯 Honeypot Traps
              <span className="text-sm text-muted-foreground ml-2">Hackers who access these paths get INSTANTLY banned</span>
            </h3>
            
            <div className="p-4 bg-purple-500/10 rounded-xl mb-4">
              <h4 className="font-medium mb-2">Active Honeypot Paths:</h4>
              <div className="flex flex-wrap gap-2">
                {honeypotPaths.map((path, i) => (
                  <span key={i} className="px-3 py-1 bg-purple-500/20 rounded-lg font-mono text-sm text-purple-300">
                    {path}
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-red-400">🚨 Caught Hackers ({honeypotVictims.length})</h4>
              {honeypotVictims.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No hackers caught yet. The trap is set! 🍯</p>
              ) : honeypotVictims.map((v, i) => (
                <div key={i} className="p-4 bg-red-500/10 rounded-xl border border-red-500/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-mono font-bold text-red-400">{v.ip}</span>
                      <span className="ml-2 text-sm text-muted-foreground">tried to access</span>
                      <span className="ml-2 font-mono text-yellow-400">{v.path}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{new Date(v.caught_at).toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 truncate">User Agent: {v.user_agent}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TARPIT TAB */}
      {activeTab === 'tarpit' && (
        <div className="space-y-6">
          <div className="bg-card/60 rounded-2xl p-6 border border-orange-500/30">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Clock className="w-6 h-6 text-orange-400" /> 🐌 Tarpit (Slow Down Attackers)
              <span className="text-sm text-muted-foreground ml-2">Make every request painfully slow</span>
            </h3>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                placeholder="IP Address"
                value={tarpitIp}
                onChange={(e) => setTarpitIp(e.target.value)}
                className="flex-1 px-4 py-3 rounded-xl bg-secondary border border-border/30"
              />
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-secondary border border-border/30">
                <span className="text-sm text-muted-foreground">Delay:</span>
                <input
                  type="number"
                  value={tarpitDelay}
                  onChange={(e) => setTarpitDelay(Math.min(30, Math.max(1, parseInt(e.target.value) || 5)))}
                  className="w-16 px-2 py-1 rounded bg-background border border-border text-center"
                  min={1}
                  max={30}
                />
                <span className="text-sm">seconds</span>
              </div>
              <Button onClick={addToTarpit} className="bg-orange-500 hover:bg-orange-600 text-black px-6">
                <Clock className="w-4 h-4 mr-2" /> Add to Tarpit
              </Button>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Tarpitted IPs ({tarpitList.length})</h4>
              {tarpitList.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground">No IPs being slowed down</p>
              ) : tarpitList.map((t, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-orange-500/10 rounded-xl border border-orange-500/20">
                  <div>
                    <span className="font-mono font-bold">{t.ip}</span>
                    <span className="ml-3 text-orange-400 font-bold">+{t.delay_seconds}s per request</span>
                  </div>
                  <Button onClick={() => removeFromTarpit(t.ip)} variant="ghost" className="text-green-400">
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* IP LOOKUP TAB */}
      {activeTab === 'lookup' && (
        <div className="space-y-6">
          <div className="bg-card/60 rounded-2xl p-6 border border-cyan-500/30">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Search className="w-6 h-6 text-cyan-400" /> 🔍 IP Lookup
              <span className="text-sm text-muted-foreground ml-2">Get full info about any IP</span>
            </h3>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                placeholder="Enter IP address..."
                value={ipLookup}
                onChange={(e) => setIpLookup(e.target.value)}
                className="flex-1 px-4 py-3 rounded-xl bg-secondary border border-border/30"
              />
              <Button onClick={lookupIp} className="bg-cyan-500 hover:bg-cyan-600 text-black px-6">
                <Search className="w-4 h-4 mr-2" /> Lookup
              </Button>
            </div>

            {ipLookupResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-secondary/50 rounded-xl">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Location
                    </h4>
                    <p><strong>Country:</strong> {ipLookupResult.geo?.country || 'N/A'} ({ipLookupResult.geo?.countryCode})</p>
                    <p><strong>City:</strong> {ipLookupResult.geo?.city || 'N/A'}</p>
                    <p><strong>ISP:</strong> {ipLookupResult.geo?.isp || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-secondary/50 rounded-xl">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Status
                    </h4>
                    <p><strong>Permanent Ban:</strong> {ipLookupResult.status?.permanently_banned ? '🚫 YES' : '✅ No'}</p>
                    <p><strong>Temp Block:</strong> {ipLookupResult.status?.temp_blocked ? '⏱️ YES' : '✅ No'}</p>
                    <p><strong>Tarpitted:</strong> {ipLookupResult.status?.tarpitted ? `🐌 +${ipLookupResult.status.tarpit_delay_seconds}s` : '✅ No'}</p>
                    <p><strong>Honeypot:</strong> {ipLookupResult.status?.honeypot_victim ? '🍯 CAUGHT!' : '✅ No'}</p>
                    <p><strong>Threat Score:</strong> {ipLookupResult.status?.threat_score || 0}</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={() => blockIp(ipLookup)} className="bg-yellow-500 text-black">Block 24h</Button>
                  <Button onClick={() => { setPermanentBanIp(ipLookup); setActiveTab('permanent'); }} className="bg-red-500">Permanent Ban</Button>
                  <Button onClick={() => { setTarpitIp(ipLookup); setActiveTab('tarpit'); }} className="bg-orange-500 text-black">Tarpit</Button>
                </div>

                {ipLookupResult.recent_attacks?.length > 0 && (
                  <div className="p-4 bg-red-500/10 rounded-xl">
                    <h4 className="font-medium mb-2 text-red-400">Recent Attacks ({ipLookupResult.recent_attacks.length})</h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {ipLookupResult.recent_attacks.map((a, i) => (
                        <div key={i} className="text-sm">
                          <span className="text-muted-foreground">{new Date(a.timestamp).toLocaleString()}</span>
                          <span className="ml-2">{a.event_type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* AUDIT LOG TAB */}
      {activeTab === 'logs' && (
        <div className="bg-card/60 rounded-2xl p-6 border border-border/30">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-400" /> Security Audit Log
          </h3>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card">
                <tr className="border-b border-border/30">
                  <th className="text-left py-3 px-4">Time</th>
                  <th className="text-left py-3 px-4">Event</th>
                  <th className="text-left py-3 px-4">Email</th>
                  <th className="text-left py-3 px-4">IP</th>
                  <th className="text-left py-3 px-4">Level</th>
                </tr>
              </thead>
              <tbody>
                {securityLogs.map((log, i) => (
                  <tr key={i} className="border-b border-border/10 hover:bg-secondary/20">
                    <td className="py-3 px-4 font-mono text-xs">{new Date(log.timestamp).toLocaleString()}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        log.event_type?.includes('success') ? 'bg-green-500/20 text-green-300' :
                        log.event_type?.includes('failed') ? 'bg-red-500/20 text-red-300' :
                        log.event_type?.includes('blocked') || log.event_type?.includes('banned') ? 'bg-yellow-500/20 text-yellow-300' :
                        'bg-blue-500/20 text-blue-300'
                      }`}>
                        {log.event_type?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4">{log.email || '-'}</td>
                    <td className="py-3 px-4 font-mono text-xs">{log.ip || '-'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs ${
                        log.threat_level === 'critical' ? 'bg-red-500/30 text-red-300' :
                        log.threat_level === 'high' ? 'bg-orange-500/30 text-orange-300' :
                        'bg-gray-500/20 text-gray-300'
                      }`}>
                        {log.threat_level || 'low'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
